import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { verifyEmailOTP, register } from './authSlice';
import api from "../utils/api";

// ── Set PIN 
export const setPin = createAsyncThunk(
  'pin/set',
  async ({ pin }, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/set-pin', { pin });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set PIN');
    }
  }
);

// ── Verify PIN (payment gate)
export const verifyPin = createAsyncThunk(
  'pin/verify',
  async ({ pin }, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/verify-pin', { pin });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'PIN verification failed');
    }
  }
);

// ── Reset PIN (knows current PIN) 
export const resetPin = createAsyncThunk(
  'pin/reset',
  async ({ currentPin, newPin }, { rejectWithValue }) => {
    try {
      const response = await api.put('/pin/reset-pin', { currentPin, newPin });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset PIN');
    }
  }
);

// ── Forgot PIN (post-OTP identity verified) ───────────────────────────────────
export const forgotPin = createAsyncThunk(
  'pin/forgot',
  async ({ newPin, confirmPin }, { rejectWithValue }) => {
    try {
      const response = await api.put('/pin/forgot-pin', { newPin, confirmPin });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset PIN');
    }
  }
);

export const sendForgotPinOtp = createAsyncThunk(
  'pin/sendForgotOtp',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/forgot-pin/send-otp');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const verifyForgotPinOtp = createAsyncThunk(
  'pin/verifyForgotOtp',
  async ({ otp }, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/forgot-pin/verify-otp', { otp });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Invalid OTP');
    }
  }
);

export const sendForgotPinEmail = createAsyncThunk(
  'pin/sendForgotEmail',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/forgot-pin/send-email-otp');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send email');
    }
  }
);

export const verifyForgotEmailOTP = createAsyncThunk(
  'pin/verifyForgotEmailOTP',
  async ({ otp }, { rejectWithValue }) => {
    try {
      const response = await api.post('/pin/forgot-pin/verify-email-otp', { otp });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Invalid OTP');
    }
  }
);

const pinSlice = createSlice({
  name: 'pin',
  initialState: {
    isVerified: false,
    isPinSet: false,         // flip to true after setPin succeeds
    status: 'idle',          // 'idle' | 'loading' | 'success' | 'failed'
    verifyStatus: 'idle',
    otpStatus: 'idle',
    otpVerified: false,
    error: null,
  },
  reducers: {
    clearPinError: (state) => {
      state.error = null;
    },
    setPinSet: (state, action) => {
      state.isPinSet = action.payload;
    },
    clearVerifyStatus: (state) => {
      state.isVerified = false;
      state.verifyStatus = 'idle';
      state.error = null;
    },
    resetPinState: (state) => {
      state.isVerified = false;
      state.status = 'idle';
      state.verifyStatus = 'idle';
      state.error = null;
    },
    clearOtpStatus: (state) => {
      state.otpStatus = 'idle';
      state.otpVerified = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ── setPin
    builder
      .addCase(setPin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(setPin.fulfilled, (state) => {
        state.status = 'success';
        state.isPinSet = true;
      })
      .addCase(setPin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

    // ── verifyPin 
    builder
      .addCase(verifyPin.pending, (state) => {
        state.verifyStatus = 'loading';
        state.isVerified = false;
        state.error = null;
      })
      .addCase(verifyPin.fulfilled, (state) => {
        state.verifyStatus = 'success';
        state.isVerified = true;
      })
      .addCase(verifyPin.rejected, (state, action) => {
        state.verifyStatus = 'failed';
        state.isVerified = false;
        state.error = action.payload;
      });

    // ── resetPin 
    builder
      .addCase(resetPin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetPin.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(resetPin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

    // ── forgotPin 
    builder
      .addCase(forgotPin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(forgotPin.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(forgotPin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

    builder
      .addCase(sendForgotPinOtp.pending, (state) => {
        state.otpStatus = 'loading';
        state.error = null;
      })
      .addCase(sendForgotPinOtp.fulfilled, (state) => {
        state.otpStatus = 'success';
      })
      .addCase(sendForgotPinOtp.rejected, (state, action) => {
        state.otpStatus = 'failed';
        state.error = action.payload;
      });

    builder
      .addCase(verifyForgotPinOtp.pending, (state) => {
        state.otpStatus = 'loading';
        state.error = null;
      })
      .addCase(verifyForgotPinOtp.fulfilled, (state) => {
        state.otpStatus = 'success';
        state.otpVerified = true;
      })
      .addCase(verifyForgotPinOtp.rejected, (state, action) => {
        state.otpStatus = 'failed';
        state.error = action.payload;
      });

    builder
      .addCase(verifyEmailOTP.fulfilled, (state, action) => {
        const entity = action.payload.user || action.payload.runner;
        if (entity?.hasPinSet !== undefined) {
          state.isPinSet = entity.hasPinSet;
        }
      })
      .addCase(register.fulfilled, (state, action) => {
        const entity = action.payload.user || action.payload.runner;
        if (entity?.hasPinSet !== undefined) {
          state.isPinSet = entity.hasPinSet;
        }
      });
    builder
      .addCase(sendForgotPinEmail.pending, (state) => {
        state.otpStatus = 'loading';
        state.error = null;
      })
      .addCase(sendForgotPinEmail.fulfilled, (state) => {
        state.otpStatus = 'success';
      })
      .addCase(sendForgotPinEmail.rejected, (state, action) => {
        state.otpStatus = 'failed';
        state.error = action.payload;
      });
    builder
      .addCase(verifyForgotEmailOTP.pending, (state) => {
        state.otpStatus = 'loading';
        state.error = null;
      })
      .addCase(verifyForgotEmailOTP.fulfilled, (state) => {
        state.otpStatus = 'success';
        state.otpVerified = true;
      })
      .addCase(verifyForgotEmailOTP.rejected, (state, action) => {
        state.otpStatus = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearPinError, clearVerifyStatus, resetPinState, clearOtpStatus, setPinSet } = pinSlice.actions;
export default pinSlice.reducer;